/**
 * Pure geometry for FloatingPanel: viewport clamp, default placement, drag,
 * resize and keyboard steps. No DOM access; every function takes the viewport.
 */
import { KEYBOARD_STEP_PX, RESIZE_DIRECTIONS } from "./floatingPanelConstants";

const clampNumber = (value, lo, hi) => Math.max(lo, Math.min(hi, value));

/**
 * Keep a size within [min, viewport]. When the viewport is smaller than the
 * minimum the viewport wins, so the panel never overflows the screen.
 */
export function clampSize(size, minSize, viewport) {
  const maxW = Math.max(1, viewport.width);
  const maxH = Math.max(1, viewport.height);
  return {
    width: clampNumber(size.width, Math.min(minSize.width, maxW), maxW),
    height: clampNumber(size.height, Math.min(minSize.height, maxH), maxH),
  };
}

/**
 * Keep a position so at least `margin` px of the panel stays inside the
 * viewport horizontally, and the header row (top edge) stays on screen:
 * top is never above 0 and never lower than `viewport.height - margin`.
 * The app header is NOT avoided here: a moved panel may sit over it.
 */
export function clampPosition(pos, size, viewport, margin) {
  const minX = margin - size.width;
  const maxX = Math.max(minX, viewport.width - margin);
  const maxY = Math.max(0, viewport.height - margin);
  return {
    x: clampNumber(pos.x, minX, maxX),
    y: clampNumber(pos.y, 0, maxY),
  };
}

/**
 * First open: top right of the area below the app header (`minTop`), `margin`
 * px in from the right edge; without a header, `margin` px from the top.
 */
export function defaultTopRightPosition(size, viewport, margin, minTop = 0) {
  return {
    x: Math.max(margin, viewport.width - size.width - margin),
    y: Math.max(margin, minTop),
  };
}

/**
 * Safe top for the panel: the lowest bottom edge of the app header bars plus
 * `gap`, or 0 when no header bar is laid out (zero-size rects are ignored).
 */
export function headerBottomOf(rects) {
  return rects.reduce((low, r) => (r && (r.width > 0 || r.height > 0) ? Math.max(low, r.bottom) : low), 0);
}

export const safeTopFor = (headerBottom, gap) => (headerBottom > 0 ? headerBottom + gap : 0);

/** Position after a pointer drag of (dx, dy) from `origin`. */
export function dragPosition(origin, dx, dy) {
  return { x: origin.x + dx, y: origin.y + dy };
}

/**
 * Size after a resize drag. `right` changes only the width, `bottom` only the
 * height, `corner` both — the same rule as the legacy map window.
 */
export function resizeSize(origin, direction, dx, dy, minSize) {
  return {
    width: direction !== RESIZE_DIRECTIONS.BOTTOM ? Math.max(minSize.width, origin.width + dx) : origin.width,
    height: direction !== RESIZE_DIRECTIONS.RIGHT ? Math.max(minSize.height, origin.height + dy) : origin.height,
  };
}

const ARROW_DELTAS = Object.freeze({
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
});

export const isArrowKey = (key) => Object.prototype.hasOwnProperty.call(ARROW_DELTAS, key);

/**
 * Keyboard step: arrows move by `step`, Shift+arrows resize by `step`
 * (right/down grow, left/up shrink). Returns the new {pos, size}.
 */
export function keyboardStep({ pos, size }, key, shiftKey, minSize, step = KEYBOARD_STEP_PX) {
  const delta = ARROW_DELTAS[key];
  if (!delta) return { pos, size };
  const [dx, dy] = delta;
  if (shiftKey) {
    return {
      pos,
      size: {
        width: Math.max(minSize.width, size.width + dx * step),
        height: Math.max(minSize.height, size.height + dy * step),
      },
    };
  }
  return { pos: { x: pos.x + dx * step, y: pos.y + dy * step }, size };
}

/** Apply both clamps; `margin == null` disables the viewport clamp entirely. */
export function clampRect(pos, size, minSize, viewport, margin) {
  if (margin == null) return { pos, size };
  const nextSize = clampSize(size, minSize, viewport);
  return { pos: clampPosition(pos, nextSize, viewport, margin), size: nextSize };
}

export const readViewport = () => ({
  width: typeof window === "undefined" ? 0 : window.innerWidth,
  height: typeof window === "undefined" ? 0 : window.innerHeight,
});
